import { useEffect, useRef, useState } from 'react';
import type { IJodit } from 'jodit/esm/types/jodit';

/** Jodit and the plugins beyond its basic ESM set, loaded the first time an editor shows. */
let loading: Promise<typeof import('jodit/esm/index.js')> | undefined;
function loadJodit() {
  loading ??= (async () => {
    const mod = await import('jodit/esm/index.js');
    await Promise.all([
      import('jodit/es2021/jodit.min.css'),
      import('jodit/esm/plugins/source/source.js'),
      import('jodit/esm/plugins/clipboard/clipboard.js'),
      import('jodit/esm/plugins/paste/paste.js'),
      import('jodit/esm/plugins/paste-from-word/paste-from-word.js'),
      import('jodit/esm/plugins/clean-html/clean-html.js'),
      import('jodit/esm/plugins/justify/justify.js'),
      import('jodit/esm/plugins/hr/hr.js'),
      import('jodit/esm/plugins/fullsize/fullsize.js'),
      import('jodit/esm/plugins/select/select.js'),
      import('jodit/esm/plugins/resize-handler/resize-handler.js'),
      import('jodit/esm/plugins/mobile/mobile.js'),
    ]);
    // Jodit has no Danish; Norwegian reads the same to a Dane.
    const no = await import('jodit/esm/langs/no.js');
    (mod.Jodit.lang as Record<string, unknown>).no = no.default;
    return mod;
  })();
  return loading;
}

/** Text from 3x or the old textarea has no tags: its blank lines become paragraphs, like the storefront shows it. */
function asHtml(v: string) {
  if (!v || /<[a-z][\s\S]*>/i.test(v)) return v;
  return v
    .split(/\n{2,}/)
    .map((p) => `<p>${p.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/\n/g, '<br>')}</p>`)
    .join('');
}

/** WYSIWYG editor for admin-authored HTML (product descriptions). */
export function HtmlEditor({ value, onChange, height = 320 }: { value: string; onChange: (html: string) => void; height?: number }) {
  const host = useRef<HTMLTextAreaElement>(null);
  const editor = useRef<IJodit | null>(null);
  const last = useRef(value);
  const cb = useRef(onChange);
  cb.current = onChange;
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let inst: IJodit | undefined;
    loadJodit()
      .then(({ Jodit }) => {
        if (!alive || !host.current) return;
        inst = Jodit.make(host.current, {
          language: 'no',
          height,
          minHeight: 160,
          toolbarAdaptive: true,
          toolbarSticky: false,
          showCharsCounter: false,
          showWordsCounter: false,
          showXPathInStatusbar: false,
          askBeforePasteHTML: false,
          askBeforePasteFromWord: false,
          defaultActionOnPaste: 'insert_clear_html',
          buttons: ['paragraph', 'bold', 'italic', 'underline', 'strikethrough', '|', 'ul', 'ol', '|', 'link', 'table', 'hr', '|', 'left', 'center', 'right', '|', 'eraser', 'undo', 'redo', '|', 'source', 'fullsize'],
          disablePlugins: ['about', 'powered-by-jodit', 'stat', 'iframe'],
        });
        inst.value = asHtml(last.current);
        inst.events.on('change', (html: string) => {
          // An empty editor leaves "<p><br></p>"; store it as empty.
          const v = html.replace(/^(\s*<p>(\s|&nbsp;|<br\s*\/?>)*<\/p>\s*)+$/i, '');
          if (v === last.current) return;
          last.current = v;
          cb.current(v);
        });
        editor.current = inst;
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      editor.current = null;
      inst?.destruct();
    };
  }, [height]);

  // A new value from outside (another language, a reload) replaces the content.
  useEffect(() => {
    if (value === last.current) return;
    last.current = value;
    if (editor.current) editor.current.value = asHtml(value);
  }, [value]);

  if (failed) return <textarea className="input font-mono text-xs" rows={8} value={value} onChange={(e) => onChange(e.target.value)} />;
  return (
    <div className="jodit-host text-sm">
      <textarea ref={host} defaultValue={asHtml(value)} />
    </div>
  );
}
